/**
 * WebUSB ESC/POS thermal printer driver for Café 24.
 * Works on Chrome (Windows, Android, Linux, macOS) — NOT Safari/iOS.
 * Renders receipt as monochrome raster bitmap → supports Arabic & emojis flawlessly.
 */

const ESC = 0x1B;
const GS = 0x1D;
const PRINTER_WIDTH_DOTS = 576;  // standard 80mm thermal @ 203dpi (72mm printable area)

let device = null;
let outEndpoint = null;
let outInterface = null;

export const isWebUSBSupported = () => typeof navigator !== "undefined" && "usb" in navigator;

async function findOutEndpoint(dev) {
    if (!dev.configuration) return null;
    for (const iface of dev.configuration.interfaces) {
        for (const alt of iface.alternates) {
            for (const ep of alt.endpoints) {
                if (ep.direction === "out") {
                    return { interfaceNumber: iface.interfaceNumber, endpointNumber: ep.endpointNumber };
                }
            }
        }
    }
    return null;
}

async function attach(dev) {
    if (!dev.opened) await dev.open();
    if (dev.configuration === null) await dev.selectConfiguration(1);
    const ep = await findOutEndpoint(dev);
    if (!ep) throw new Error("No OUT endpoint on this USB device");
    try {
        await dev.claimInterface(ep.interfaceNumber);
    } catch (e) {
        // Some Xprinter models need detachKernelDriver; on browser-side just retry
        if (String(e).includes("already")) { /* fine */ } else { throw e; }
    }
    device = dev;
    outEndpoint = ep.endpointNumber;
    outInterface = ep.interfaceNumber;
    return dev;
}

/** Show the USB device picker — must be called from a user gesture (button click). */
export async function connectPrinter() {
    if (!isWebUSBSupported()) throw new Error("WebUSB not supported in this browser. Use Chrome on Windows or Android.");
    // Empty filters → user picks any USB device
    const dev = await navigator.usb.requestDevice({ filters: [] });
    return attach(dev);
}

/** Try to silently reattach to a previously-authorized printer (no user gesture needed). */
export async function autoReconnect() {
    if (!isWebUSBSupported()) return false;
    try {
        const devices = await navigator.usb.getDevices();
        if (devices.length === 0) return false;
        await attach(devices[0]);
        return true;
    } catch (e) {
        device = null;
        return false;
    }
}

export function isPrinterConnected() {
    return !!device && device.opened && outEndpoint !== null;
}

export function getPrinterInfo() {
    if (!device) return null;
    return {
        vendorId: device.vendorId,
        productId: device.productId,
        productName: device.productName || "USB Printer",
        manufacturerName: device.manufacturerName || "",
    };
}

export async function disconnectPrinter() {
    if (!device) return;
    try {
        if (outInterface !== null) await device.releaseInterface(outInterface);
        await device.close();
    } catch (e) { /* ignore */ }
    device = null;
    outEndpoint = null;
    outInterface = null;
}

async function send(bytes) {
    if (!isPrinterConnected()) throw new Error("Printer not connected");
    // chunk to avoid USB transfer overflow on some platforms
    const CHUNK = 4096;
    for (let i = 0; i < bytes.length; i += CHUNK) {
        await device.transferOut(outEndpoint, bytes.slice(i, Math.min(i + CHUNK, bytes.length)));
    }
}

/**
 * Render a DOM element to thermal printer via raster bitmap.
 * The element must already be sized to PRINTER_WIDTH_DOTS pixels wide.
 */
export async function printDOMElement(element) {
    if (!isPrinterConnected()) throw new Error("Printer not connected");
    const html2canvas = (await import("html2canvas")).default;

    const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        width: PRINTER_WIDTH_DOTS,
        scale: 1,
        useCORS: true,
        logging: false,
    });

    // ensure width is a multiple of 8 (ESC/POS requirement)
    const w = Math.floor(canvas.width / 8) * 8;
    const h = canvas.height;
    const ctx = canvas.getContext("2d");
    const { data } = ctx.getImageData(0, 0, w, h);
    const bytesPerRow = w / 8;
    const bitmap = new Uint8Array(bytesPerRow * h);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const idx = (y * w + x) * 4;
            const lum = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
            if (lum < 140) {
                const byteIdx = y * bytesPerRow + (x >> 3);
                bitmap[byteIdx] |= 0x80 >> (x & 7);
            }
        }
    }

    // ESC @ (initialize)
    await send(new Uint8Array([ESC, 0x40]));
    // Some printers have a max raster height per command — split into bands
    const BAND_HEIGHT = 128;
    let yPos = 0;
    while (yPos < h) {
        const bandH = Math.min(BAND_HEIGHT, h - yPos);
        const bandBytes = bitmap.slice(yPos * bytesPerRow, (yPos + bandH) * bytesPerRow);
        const xL = bytesPerRow & 0xff;
        const xH = (bytesPerRow >> 8) & 0xff;
        const yL = bandH & 0xff;
        const yH = (bandH >> 8) & 0xff;
        // GS v 0 m xL xH yL yH data  (m=0 normal)
        const header = new Uint8Array([GS, 0x76, 0x30, 0x00, xL, xH, yL, yH]);
        const payload = new Uint8Array(header.length + bandBytes.length);
        payload.set(header, 0);
        payload.set(bandBytes, header.length);
        await send(payload);
        yPos += bandH;
    }

    // feed 4 lines + full cut
    await send(new Uint8Array([ESC, 0x64, 4]));
    await send(new Uint8Array([GS, 0x56, 0x00]));
}

/** Test print — small "Café 24 OK" sample. Useful from Settings. */
export async function testPrint() {
    if (!isPrinterConnected()) throw new Error("Printer not connected");
    const div = document.createElement("div");
    div.style.cssText = `position:fixed;left:-99999px;top:0;width:576px;background:#fff;color:#000;font-family:'Cairo',sans-serif;padding:20px;`;
    div.innerHTML = `
        <div style="text-align:center;font-size:42px;font-weight:700;letter-spacing:2px;">Café 24</div>
        <div style="text-align:center;font-size:36px;font-weight:700;margin-top:4px;">كافيه ٢٤</div>
        <div style="border-top:2px dashed #000;margin:18px 0;"></div>
        <div style="text-align:center;font-size:24px;">Printer test — اختبار الطابعة</div>
        <div style="text-align:center;font-size:20px;margin-top:10px;color:#444;">${new Date().toLocaleString("ar-SD")}</div>
        <div style="border-top:2px dashed #000;margin:18px 0;"></div>
        <div style="text-align:center;font-size:22px;">✓ OK</div>
    `;
    document.body.appendChild(div);
    try {
        // wait a tick for fonts to flow
        await new Promise((r) => setTimeout(r, 80));
        await printDOMElement(div);
    } finally {
        document.body.removeChild(div);
    }
}
